import { Box, Typography } from "@mui/material";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LabelList,
    ReferenceArea,
} from "recharts";
import React from "react";

const data = [
    { year: 2008, value: 1.0 },
    { year: 2009, value: 1.7 },
    { year: 2010, value: 0.9 },
    { year: 2011, value: 0.7 },
    { year: 2012, value: 1.0 },
    { year: 2013, value: 0.8 },
    { year: 2014, value: 1.2 },
    { year: 2015, value: 0.8 },
    { year: 2016, value: 0.1 },
    { year: 2017, value: 0.5 },
    { year: 2018, value: 1.6 },
    { year: 2019, value: -0.5 },
    { year: 2020, value: 0.8 },
    { year: 2021, value: 0.0 },
    { year: 2022, value: -1.9 },
    { year: 2023, value: -2.7 },
    { year: 2024, value: 5.2 },
    { year: 2025, value: -1.4 },
];

// Label custom pour afficher les valeurs (rouge si négatif)
const ResultLabel: React.FC<any> = (props) => {
    const { x, y, value } = props;
    if (value === undefined || value === null) return null;

    const text = String(value).replace(".", ",");
    const fill = value < 0 ? "#b71c1c" : "#000";

    return (
        <text
            x={x + 20}
            y={y - 6}
            textAnchor="middle"
            fontSize={11}
            fill={fill}
            fontWeight={value < 0 ? 700 : 500}
        >
            {text}
        </text>
    );
};

const Tract3G4: React.FC = () => {
    return (
        <Box sx={{ position: "relative", height: 300 }}>
            {/* Graphique principal */}
            <ResponsiveContainer width="100%" height="70%">
                <BarChart
                    data={data}
                    margin={{ top: 40, right: 20, left: 20, bottom: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="year" />
                    <YAxis domain={[-3, 6]} />
                    <Tooltip formatter={(v) => `${v} M€`} />

                    {/* Zone actuelle du maire */}
                    <ReferenceArea
                        x1={2018}
                        x2={2025}
                        fill="#e5e0ff"
                        fillOpacity={0.9}
                    />

                    {/* Barres résultat annuel */}
                    <Bar dataKey="value" fill="#28313f">
                        <LabelList dataKey="value" content={(props) => <ResultLabel {...props} />} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/* Pastilles de cumul par mandature */}
            {/* +6,1M€ (2008–2013) en vert */}
            <Box
                sx={{
                    position: "absolute",
                    top: 16,
                    left: "20%",
                    px: 2,
                    py: 0.3,
                    bgcolor: "#7cb342",
                    borderRadius: 999,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                +6,1M€
            </Box>

            {/* +2,6M€ (2014–2017) en vert */}
            <Box
                sx={{
                    position: "absolute",
                    top: 16,
                    left: "46%",
                    px: 2,
                    py: 0.3,
                    bgcolor: "#7cb342",
                    borderRadius: 999,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                +2,6M€
            </Box>

            {/* +1,1M€ (2018–2021) en jaune */}
            <Box
                sx={{
                    position: "absolute",
                    top: 16,
                    left: "68%",
                    px: 2,
                    py: 0.3,
                    bgcolor: "#fbc02d",
                    borderRadius: 999,
                    color: "#000",
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                +1,1M€
            </Box>

            {/* +0M€ (2022–2025) en rouge */}
            <Box
                sx={{
                    position: "absolute",
                    top: 16,
                    left: "86%",
                    px: 2,
                    py: 0.3,
                    bgcolor: "#b71c1c",
                    borderRadius: 999,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                }}
            >
                +0M€
            </Box>

            {/* Libellé "M. Dintilhac maire" */}
            <Box
                sx={{
                    position: "absolute",
                    top: "72%",
                    left: "55%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        width: 26,
                        height: 10,
                        bgcolor: "#e5e0ff",
                        borderRadius: 1,
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", fontWeight: 600, color: "#444" }}
                >
                    M. Dintilhac maire
                </Typography>
            </Box>

            {/* Légende "+XM€ Cumul ..." */}
            <Box
                sx={{
                    position: "absolute",
                    top: "72%",
                    left: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        px: 1.6,
                        py: 0.2,
                        borderRadius: 999,
                        border: "1px solid #000",
                        fontSize: 11,
                        fontWeight: 700,
                    }}
                >
                    +XM€
                </Box>
                <Typography variant="caption">
                    Cumul des résultats annuels pour chaque mandature
                </Typography>
            </Box>

            {/* Note explicative (adaptée) */}
            <Box
                sx={{
                    position: "absolute",
                    top: "86%",
                    left: 0,
                    right: 0,
                }}
            >
                <Typography variant="caption">
                    A noter qu’en 2024, l’excédent provient majoritairement d’un emprunt de
                    3,5 M€ (comptabilisé en recettes) et de subventions. Un résultat en
                    trompe-l’œil, qui s’accompagne d’une hausse de la dette.
                </Typography>
            </Box>
        </Box>
    );
};

export default Tract3G4;
